import { IValidationFunction } from "aic";
import { eHierarchyLevel } from "./api/enums";
import { parentHierarchyLevel } from "./parentHierarchyLevel";

/**
 * Pattern that allows only letters, digits, spaces, underscores, and hyphens.
 */
export const allowedHierarchyNamePattern = /^[A-Za-z0-9_ -]+$/;

/**
 * Validates a hierarchy name to ensure it is not empty, uses supported characters,
 * and is not already in use among the sibling hierarchies provided via `existingValues`.
 *
 * Uniqueness is checked entirely on the frontend using the sibling names passed in
 * `target.options.existingValues`. Callers are responsible for providing the correct
 * list of sibling names (excluding the current hierarchy when editing).
 *
 * @param _backEndUrl - Unused; kept for interface compatibility.
 * @param target - Validation target containing the entered value and options.
 *        `options.existingValues` should be the sibling hierarchy names to check for duplicates.
 *        `options.otherValues.level` (optional) is used to produce a level-aware error message.
 * @param _controller - Unused; kept for interface compatibility.
 * @returns Validation result containing validity state and message text.
 */
export const hierarchyNameValidator: IValidationFunction = async (
  _backEndUrl: string,
  target,
  _controller: AbortController,
) => {
  const hierarchyNameNormalized = target.value.trim().toLowerCase();
  const level = target.options?.otherValues?.level as eHierarchyLevel | undefined;

  if (hierarchyNameNormalized === "") {
    return { success: true, data: { valid: false, validationText: "Name cannot be empty" } };
  }

  if (!allowedHierarchyNamePattern.test(hierarchyNameNormalized)) {
    return {
      success: true,
      data: {
        valid: false,
        validationText: "Name may only contain letters, numbers, spaces, hyphens, and underscores",
      },
    };
  }

  const existingValues = target.options?.existingValues ?? [];
  const isDuplicate = existingValues.some(
    (v) => v.trim().toLowerCase() === hierarchyNameNormalized,
  );

  if (isDuplicate) {
    return {
      success: true,
      data: {
        valid: false,
        validationText: !level
          ? "Hierarchy name is already used within current parent hierarchy"
          : `${level} name is already used within current ${parentHierarchyLevel(level)}`,
      },
    };
  }

  return { success: true, data: { valid: true, validationText: "" } };
};
